import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, ClipboardList, History, FilePlus, GraduationCap, ArrowRight, Clock,
} from 'lucide-react';

import SafetyNotice from '../components/job-brief/SafetyNotice';
import { jobBriefV2Styles } from '../components/job-brief/styles';
import { jobBriefApi } from '../services/jobBriefApi';

const CHOICES = [
  { id: 'guided',      title: 'Start Guided Brief',   desc: 'AskStewie walks you through Plan, Perform, and Close Out.', Icon: ClipboardList, primary: true,  color: '#67e8f9' },
  { id: 'describe',    title: 'Describe the Job',     desc: 'Speak or type freely — Stewie extracts scope, hazards, and critical steps.', Icon: MessageCircle, color: '#a78bfa' },
  { id: 'previous',    title: 'Use a Previous Brief', desc: 'Copy a completed job as a starting template.', Icon: History, color: '#facc15' },
  { id: 'continue',    title: 'Continue Draft',       desc: 'Resume an in-progress brief exactly where you left off.', Icon: FilePlus, color: '#4ade80' },
  { id: 'training',    title: 'Training Mode',        desc: 'Learn Human Performance tools with examples and knowledge checks.', Icon: GraduationCap, color: '#fb923c' },
];

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * JobBriefV2Home — /job-briefing/v2 landing.
 * Matches the AskStewie Landing/Command Center aesthetic exactly.
 */
export default function JobBriefV2Home({ user, onLogout }) {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [creating, setCreating] = useState(false);
  // Silence unused-prop lint when AppShell owns sign-out.
  void onLogout;

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    jobBriefApi.listDrafts(user.email).then((res) => {
      if (!cancelled) setDrafts(res?.drafts || []);
    });
    return () => { cancelled = true; };
  }, [user?.email]);

  const firstName = (user?.name || user?.email || '').split(/[\s@]/)[0] || 'Steward';

  const startNewDraft = useCallback(async (mode) => {
    if (!user?.email) return;
    setCreating(true);
    try {
      const created = await jobBriefApi.createDraft({
        owner_email: user.email,
        title: mode === 'guided'   ? 'Guided Job Brief'
             : mode === 'describe' ? 'Described Job Brief'
             : mode === 'training' ? 'Training Job Brief'
             : 'Job Brief',
        phase: 'plan',
        conversational: { mode },
      });
      if (created?.id) navigate(`/job-briefing/v2/${created.id}`);
    } finally { setCreating(false); }
  }, [navigate, user?.email]);

  const openDraft = useCallback((id) => navigate(`/job-briefing/v2/${id}`), [navigate]);

  return (
    <div className="jbv2-root" data-testid="jbv2-home">
      <style>{jobBriefV2Styles}</style>

      <main className="jbv2-main">
        {/* Hero */}
        <section className="jbv2-hero" data-testid="jbv2-hero">
          <div className="jbv2-hero-content">
            <div className="jbv2-hero-eyebrow">Job Brief</div>
            <h1 className="jbv2-hero-title">Let&rsquo;s plan the job, <span style={{ color: '#facc15' }}>{firstName}</span>.</h1>
            <p className="jbv2-hero-sub">
              Describe the work, and AskStewie will help organize the scope, hazards, Human Performance tools, critical steps, and required discussion points.
            </p>
          </div>
          <div className="jbv2-hero-avatar" aria-hidden="true">
            <img src="/stewie_front.png" alt="" />
          </div>
        </section>

        <SafetyNotice />

        {/* Primary choices */}
        <section aria-labelledby="jbv2-choices-title">
          <header className="jbv2-h">
            <div className="jbv2-h-eyebrow">Start Here</div>
            <h2 id="jbv2-choices-title" className="jbv2-h-title">How would you like to begin?</h2>
            <span className="jbv2-h-underline" aria-hidden="true" />
          </header>
          <div className="jbv2-choice-grid" style={{ marginTop: 10 }}>
            {CHOICES.map((c) => {
              const Icon = c.Icon;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`jbv2-choice ${c.primary ? 'jbv2-choice--primary' : ''}`}
                  onClick={() => c.id === 'continue' ? navigate('#continue-drafts') : startNewDraft(c.id)}
                  disabled={creating && c.id !== 'continue'}
                  style={{ '--jb-c': c.color }}
                  data-testid={`jbv2-choice-${c.id}`}
                >
                  <span className="jbv2-choice-icon"><Icon size={18} strokeWidth={2.3} /></span>
                  <span className="jbv2-choice-title">{c.title}</span>
                  <span className="jbv2-choice-desc">{c.desc}</span>
                  <span style={{ marginTop: 'auto', color: c.color, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Continue <ArrowRight size={12} strokeWidth={2.5} />
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Continue drafts */}
        {drafts.length > 0 && (
          <section aria-labelledby="jbv2-recent-title" id="continue-drafts">
            <header className="jbv2-h">
              <div className="jbv2-h-eyebrow">In Progress</div>
              <h2 id="jbv2-recent-title" className="jbv2-h-title">Your recent drafts</h2>
              <span className="jbv2-h-underline" aria-hidden="true" />
            </header>
            <div className="jbv2-recent" style={{ marginTop: 10 }} data-testid="jbv2-recent-list">
              {drafts.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="jbv2-recent-item"
                  onClick={() => openDraft(d.id)}
                  data-testid={`jbv2-recent-${d.id}`}
                >
                  <Clock size={16} color="#facc15" />
                  <span className="jbv2-recent-title">{d.title || 'Untitled Job Brief'}</span>
                  <span className="jbv2-recent-meta">
                    Rev {d.revision || 1} · Updated {fmtTime(d.updated_at)}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
